"use client"

import { create } from "zustand"

import { getAuthContextState } from "@/features/auth/services/auth-context-store"
import {
  createMinisterService,
  deleteMinisterService,
  listMinistersService,
  updateMinisterService,
  uploadMinisterPhotoService,
} from "@/features/church/config/ministers/services/ministers.service"
import {
  type CreateMinisterInput,
  type Minister,
  MinisterStatus,
  type MinistersListQuery,
  type MinistersListResponse,
  type UpdateMinisterInput,
} from "@/features/church/config/ministers/types/minister.types"
import { WorkspaceScope } from "@ibada/shared-types/common"

type MinistersState = {
  ministers: Minister[]
  total: number
  page: number
  limit: number
  query: MinistersListQuery
  isLoading: boolean
  listError: unknown
  actionError: unknown
  listMinisters: (query?: MinistersListQuery) => Promise<MinistersListResponse>
  createMinister: (input: CreateMinisterInput) => Promise<Minister>
  updateMinister: (id: string, input: UpdateMinisterInput) => Promise<Minister>
  deleteMinister: (id: string) => Promise<void>
  setMinisterStatus: (id: string, status: MinisterStatus) => Promise<Minister>
  uploadMinisterPhoto: (id: string, file: File) => Promise<Minister>
  getById: (id: string) => Minister | undefined
}

const DEFAULT_LIST_LIMIT = 20

function withScope(query: MinistersListQuery): MinistersListQuery {
  const workspaceId = query.workspaceId ?? getAuthContextState().activeWorkspaceId ?? undefined
  // Workspace-wide management listing and shared listing are both not pinned to a branch.
  if (query.allBranches || query.scope === WorkspaceScope.Shared) {
    return { ...query, workspaceId, branchId: undefined }
  }
  return { ...query, workspaceId }
}

export const useMinistersStore = create<MinistersState>()((set, get) => ({
  ministers: [],
  total: 0,
  page: 1,
  limit: DEFAULT_LIST_LIMIT,
  query: {
    q: "",
    page: 1,
    limit: DEFAULT_LIST_LIMIT,
    sortBy: "displayName",
    sortOrder: "asc",
  },
  isLoading: false,
  listError: null,
  actionError: null,

  getById: (id) => get().ministers.find((item) => item.id === id),

  listMinisters: async (query) => {
    const nextQuery = withScope({ ...get().query, ...query })
    set({ isLoading: true, listError: null })
    try {
      const response = await listMinistersService(nextQuery)
      set({
        ministers: response.items,
        total: response.total,
        page: response.page,
        limit: response.limit,
        query: { ...nextQuery, page: response.page, limit: response.limit },
        isLoading: false,
        listError: null,
      })
      return response
    } catch (error) {
      set({ isLoading: false, listError: error })
      throw error
    }
  },

  createMinister: async (input) => {
    set({ isLoading: true, actionError: null })
    try {
      const workspaceId =
        getAuthContextState().activeWorkspaceId ?? get().query.workspaceId ?? undefined
      const payload: CreateMinisterInput =
        input.workspaceId === undefined ? { ...input, workspaceId } : input
      const created = await createMinisterService(payload)
      await get().listMinisters({ ...get().query })
      return created
    } catch (error) {
      set({ isLoading: false, actionError: error })
      throw error
    }
  },

  updateMinister: async (id, input) => {
    set({ isLoading: true, actionError: null })
    try {
      const updated = await updateMinisterService(id, input)
      await get().listMinisters({ ...get().query })
      return updated
    } catch (error) {
      set({ isLoading: false, actionError: error })
      throw error
    }
  },

  deleteMinister: async (id) => {
    set({ isLoading: true, actionError: null })
    try {
      await deleteMinisterService(id)
      await get().listMinisters({ ...get().query })
    } catch (error) {
      set({ isLoading: false, actionError: error })
      throw error
    }
  },

  setMinisterStatus: async (id, status) => {
    set({ isLoading: true, actionError: null })
    try {
      const updated = await updateMinisterService(id, { status })
      set((state) => ({
        ministers: state.ministers.map((item) =>
          item.id === id ? { ...item, status: updated.status, updatedAt: updated.updatedAt } : item
        ),
        isLoading: false,
        actionError: null,
      }))
      return updated
    } catch (error) {
      set({ isLoading: false, actionError: error })
      throw error
    }
  },

  uploadMinisterPhoto: async (id, file) => {
    set({ actionError: null })
    try {
      const updated = await uploadMinisterPhotoService(id, file)
      set((state) => ({
        ministers: state.ministers.map((item) => (item.id === id ? updated : item)),
      }))
      return updated
    } catch (error) {
      set({ actionError: error })
      throw error
    }
  },
}))
